import { useEffect, useRef } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, Animated } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { ChevronUp, ChevronDown, Minus } from 'lucide-react-native';
import type { LeaderboardRowV1 } from '@/lib/leaderboard';
import { getDivision } from './RankDivisionBand';
import { useUnits } from '@/lib/useUnits';
import FounderBadge from '@/components/FounderBadge';

import { calculateDOTS, lbsToKg } from '@/lib/dotsCoefficient';

const FRESHNESS_COLORS: Record<string, string> = {
  green: '#22c55e',
  blue: '#3b82f6',
  amber: '#f59e0b',
  red: '#ef4444',
};

const FRESHNESS_LABELS: Record<string, string> = {
  green: 'Fresh',
  blue: 'Aging',
  amber: 'Stale',
  red: 'Expired',
};

type SortBy = 'strength' | 'relative' | 'duelsWon' | 'volume';

type Props = {
  row: LeaderboardRowV1;
  rank: number;
  totalUsers: number;
  isCurrentUser: boolean;
  previousRank: number | null;
  onPress?: () => void;
  isGymMate?: boolean;
  sortBy?: SortBy;
  userUnits?: string;
};

export default function EnhancedLeaderboardRow({
  row,
  rank,
  totalUsers,
  isCurrentUser,
  previousRank,
  onPress,
  isGymMate = false,
  sortBy = 'strength',
  userUnits: _userUnits = 'lbs',
}: Props) {
  const { theme } = useTheme();
  const { displayWeight, userUnits: liveUnits } = useUnits();
  // Always use the live units from the hook (reads from profile directly)
  const userUnits = liveUnits;
  const bounceAnim = useRef(new Animated.Value(0)).current;
  const division = getDivision(rank, totalUsers, row.strength_score ?? undefined);

  useEffect(() => {
    if (previousRank != null && previousRank !== rank) {
      Animated.sequence([
        Animated.timing(bounceAnim, { toValue: -6, duration: 150, useNativeDriver: true }),
        Animated.spring(bounceAnim, { toValue: 0, useNativeDriver: true, speed: 12, bounciness: 14 }),
      ]).start();
    }
  }, [rank, previousRank]);
  const initial = (row.display_name ?? row.handle ?? '?')[0].toUpperCase();
  // Don't show rank changes when total users < 5 — too volatile
  const movement = previousRank != null && totalUsers >= 5 ? previousRank - rank : null;
  const freshnessBand = row.freshness_band ?? '';
  const freshColor = FRESHNESS_COLORS[freshnessBand] ?? theme.colors.textSecondary;
  const freshnessLabel = FRESHNESS_LABELS[freshnessBand] ?? '';

  return (
    <TouchableOpacity
      style={[
        styles.row,
        { backgroundColor: isCurrentUser ? theme.colors.accent + '12' : 'transparent', borderBottomColor: theme.colors.border },
      ]}
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={`Rank ${rank}, ${row.display_name ?? row.handle ?? 'Unknown'}, strength score ${row.strength_score != null ? Math.round(row.strength_score) : 0}`}
    >
      {/* Rank circle */}
      <View style={[styles.rankCircle, { backgroundColor: division.color + '33' }]}>
        <Text style={[styles.rankText, { color: division.color === '#ffd700' ? '#b8860b' : division.color }]}>
          {rank}
        </Text>
      </View>

      {/* Avatar */}
      <View style={[styles.avatar, { backgroundColor: theme.colors.border }]}>
        <Text style={[styles.avatarText, { color: theme.colors.text }]}>{initial}</Text>
      </View>

      {/* Name */}
      <View style={styles.nameBlock}>
        <View style={styles.nameRow}>
          <Text style={[styles.displayName, { color: theme.colors.text }]} numberOfLines={1}>
            {row.display_name ?? row.handle ?? 'Unknown'}
          </Text>

          <FounderBadge createdAt={row.created_at} />
        </View>
        {row.handle && (
          <Text style={[styles.handle, { color: theme.colors.textSecondary }]} numberOfLines={1}>
            @{row.handle}
          </Text>
        )}
      </View>

      {/* Movement */}
      {movement != null && (
        <Animated.View style={[styles.movement, { transform: [{ translateY: bounceAnim }] }]}>
          {movement > 0 ? (
            <>
              <Text style={{ fontSize: 11, color: '#22c55e', fontWeight: '800' }}>▲</Text>
              <Text style={{ fontSize: 11, color: '#22c55e', fontWeight: '700' }}>{movement}</Text>
            </>
          ) : movement < 0 ? (
            <>
              <Text style={{ fontSize: 11, color: '#ef4444', fontWeight: '800' }}>▼</Text>
              <Text style={{ fontSize: 11, color: '#ef4444', fontWeight: '700' }}>{Math.abs(movement)}</Text>
            </>
          ) : (
            <Minus size={12} color="#9ca3af" />
          )}
        </Animated.View>
      )}

      {/* Freshness dot */}
      <View
        style={styles.freshDotContainer}
        accessibilityLabel={freshnessLabel ? `Freshness: ${freshnessLabel}` : 'Freshness unknown'}
      >
        <View style={[styles.freshDot, { backgroundColor: freshColor }]} />
        {freshnessLabel ? (
          <Text style={[styles.freshLabel, { color: freshColor }]}>{freshnessLabel}</Text>
        ) : null}
      </View>

      {/* Score — changes based on active sort filter */}
      <View style={{ alignItems: 'flex-end', minWidth: 60 }}>
        <Text style={[styles.score, { color: theme.colors.text }]}>
          {sortBy === 'strength'
            ? (row.strength_score != null ? Math.round(row.strength_score).toLocaleString() : '—')
            : sortBy === 'relative'
            ? (() => {
                const score = row.strength_score ?? 0;
                const bw = row.bodyweight ?? 0;
                const gender = (row.gender === 'female' ? 'female' : 'male') as 'male' | 'female';
                if (score <= 0 || bw <= 0) return '—';
                return calculateDOTS(lbsToKg(score), lbsToKg(bw), gender).toFixed(0);
              })()
            : sortBy === 'volume'
            ? (row.total_volume != null ? `${Math.round(displayWeight(row.total_volume, 'lbs')).toLocaleString()}` : '—')
            : (() => {
                const total = (row.wins ?? 0) + (row.losses ?? 0);
                if (total === 0) return '—';
                return `${Math.round(((row.wins ?? 0) / total) * 100)}%`;
              })()
          }
        </Text>
        <Text style={{ fontSize: 11, color: theme.colors.textTertiary, fontWeight: '500' }}>
          {sortBy === 'strength' ? 'STR' : sortBy === 'relative' ? 'DOTS' : sortBy === 'volume' ? userUnits : ((row.wins ?? 0) + (row.losses ?? 0) === 0 ? 'NO DUELS' : `${row.wins ?? 0}W-${row.losses ?? 0}L`)}
        </Text>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 12,
    minHeight: 56,
  },
  rankCircle: {
    width: 28,
    height: 28,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rankText: { fontSize: 12, fontWeight: '800' },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: { fontSize: 14, fontWeight: '700' },
  nameBlock: { flex: 1, minWidth: 0 },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  displayName: { fontSize: 14, fontWeight: '600', flexShrink: 1 },
  handle: { fontSize: 11, fontWeight: '500' },
  youBadge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
  },
  youText: { fontSize: 11, fontWeight: '800', color: '#fff' },
  movement: { width: 16, alignItems: 'center' },
  freshDotContainer: { alignItems: 'center', gap: 2 },
  freshDot: { width: 6, height: 6, borderRadius: 3 },
  freshLabel: { fontSize: 11, fontWeight: '600', textTransform: 'capitalize' },
  score: { fontSize: 16, fontWeight: '800', minWidth: 40, textAlign: 'right' },
});
